/**
 * buildJornadaTicketPlainText.ts — Líneas térmicas del resumen global de jornada.
 *
 * Misma familia que `buildCierreTicketPlainText`: `sectionMajor`, `table2Col`,
 * `metaRow` y etiquetas abreviadas en 58mm (`labelForWidth`).
 */

import type { JornadaTicketModel } from "./buildJornadaTicketModel";
import type { JornadaCajaTicketBlock } from "./buildJornadaTicketModel";
import { gastoExternoMetodoLabel } from "./externalExpensesHelper";
import { loadBusinessForReceipt } from "./saleTicketEscPos";
import { METODOS_PROPINA, type MontosPorMetodoPago } from "./propinaAllocation";
import { getDefaultPaymentMethodLabel } from "@/lib/paymentMethodsConfig";
import {
  blank,
  centered,
  fitCell,
  isNarrowTicket,
  labelForWidth,
  majorSep,
  metaRow,
  minorSep,
  moneyTicket,
  sectionMajor,
  sectionSub,
  table2Col,
  ticketCols,
} from "./ticketCierreAsciiLayout";

export interface BuildJornadaTicketPlainTextInput {
  model: JornadaTicketModel;
  paperWidthMm?: 58 | 80;
  /** Desglose por turno (compacto automático en 58mm). */
  includeTurnos?: boolean;
}

function subtotalMontos(montos: MontosPorMetodoPago): number {
  return METODOS_PROPINA.reduce((s, k) => s + montos[k], 0);
}

function digitalTotal(montos: MontosPorMetodoPago): number {
  return montos.nequi + montos.daviplata + montos.tarjeta;
}

/** Encabezado con negocio + título (compartido por variante multicaja y de caja única). */
function appendHeader(lines: string[], w: number, title: string): void {
  const biz = loadBusinessForReceipt();
  lines.push(majorSep(w));
  lines.push(centered(biz.nombre.toUpperCase(), w));
  if (biz.slogan) lines.push(centered(biz.slogan.toUpperCase(), w));
  lines.push(majorSep(w));
  lines.push(centered(title, w));
}

/** Variante de caja única: cada turno con su resumen + ingresos por método consolidado al final. */
function appendTurnoResumen(
  lines: string[],
  turno: JornadaCajaTicketBlock["turnos"][number],
  w: number,
): void {
  const resumen = turno.resumenOperativo;
  sectionSub(lines, turno.turnoLabel.toUpperCase(), w);
  lines.push(metaRow("Horario:", turno.horario, w));
  lines.push(metaRow("Responsable:", fitCell(turno.responsable, w - 14), w));

  if (resumen.ventasActivasCount > 0) {
    const resumenRows = [
      {
        left: "Ventas realizadas",
        right: String(resumen.ventasActivasCount),
      },
      {
        left: "Artículos vendidos",
        right: String(resumen.totalArticulosVendidos),
      },
    ];
    if (turno.montosPorMetodo.efectivo > 0) {
      resumenRows.push({
        left: "Total Efectivo",
        right: moneyTicket(turno.montosPorMetodo.efectivo),
      });
    }
    const digTurno =
      turno.montosPorMetodo.nequi +
      turno.montosPorMetodo.daviplata +
      turno.montosPorMetodo.tarjeta;
    if (digTurno > 0) {
      resumenRows.push({
        left: "Total Digital",
        right: moneyTicket(digTurno),
      });
    }
    resumenRows.push({
      left: labelForWidth("Total cobrado en ventas", "Total cobrado", w),
      right: moneyTicket(turno.subtotalCobrado),
    });
    if (turno.recaudoTotal > turno.subtotalCobrado) {
      resumenRows.push({
        left: labelForWidth(
          "Recaudo total (incl. ingresos/abonos)",
          "Recaudo total (incl.)",
          w,
        ),
        right: moneyTicket(turno.recaudoTotal),
      });
    }
    if (turno.propinaTotal > 0) {
      resumenRows.push({
        left: "Propinas recibidas",
        right: moneyTicket(turno.propinaTotal),
      });
    }
    if (turno.ingresosManualesEfectivo > 0) {
      resumenRows.push({
        left: labelForWidth("+ INGRESO (Mov. de Caja)", "+ INGRESO", w),
        right: `+${moneyTicket(turno.ingresosManualesEfectivo)}`,
      });
    }
    if (turno.abonosCreditoEfectivo > 0) {
      resumenRows.push({
        left: labelForWidth(
          "+ ABONOS A CRÉDITO (efectivo)",
          "+ ABONOS A CRÉDITO",
          w,
        ),
        right: `+${moneyTicket(turno.abonosCreditoEfectivo)}`,
      });
    }
    if (turno.retiros > 0) {
      resumenRows.push({
        left: labelForWidth("− RETIRO (Mov. de Caja)", "− RETIRO", w),
        right: `-${moneyTicket(turno.retiros)}`,
      });
    }
    if (turno.gastos > 0) {
      resumenRows.push({
        left: labelForWidth("− GASTOS (Mov. de Caja)", "− GASTOS", w),
        right: `-${moneyTicket(turno.gastos)}`,
      });
    }
    if (turno.devolucionesEfectivo > 0) {
      resumenRows.push({
        left: labelForWidth("− DEVOLUCIÓN (Mov. de Caja)", "− DEVOLUCION", w),
        right: `-${moneyTicket(turno.devolucionesEfectivo)}`,
      });
    }
    lines.push(
      ...table2Col(w, "Concepto", "Valor", resumenRows, {
        closeBottom: false,
      }),
    );
    if (resumen.horaPicoRango) {
      lines.push(metaRow("Hora pico:", resumen.horaPicoRango, w));
      lines.push(
        ` (${resumen.horaPicoVentasCount} ventas · ${moneyTicket(resumen.horaPicoMonto)} en esa hora)`.slice(
          0,
          w,
        ),
      );
    }
    {
      const labelArqueo =
        turno.diferenciaArqueo < 0
          ? "FALTANTE FISICO"
          : turno.diferenciaArqueo > 0
            ? "SOBRANTE FISICO"
            : "ARQUEO CUADRADO";
      const sign = turno.diferenciaArqueo > 0 ? "+" : "-";
      lines.push(
        ...table2Col(
          w,
          labelForWidth("Resultado del arqueo", "Resultado arqueo", w),
          "Valor",
          [
            {
              left: labelForWidth(
                "Efectivo esperado en gaveta",
                "Esperado gaveta",
                w,
              ),
              right: moneyTicket(turno.esperadoGaveta),
            },
            {
              left: labelForWidth("Efectivo contado", "Contado", w),
              right: moneyTicket(turno.contadoReal),
            },
            {
              left: labelArqueo,
              right:
                turno.diferenciaArqueo === 0
                  ? moneyTicket(0)
                  : `${sign}${moneyTicket(Math.abs(turno.diferenciaArqueo))}`,
            },
          ],
          { closeBottom: false },
        ),
      );
    }
  }
  lines.push(blank(w));
}

function buildSingleCajaPlainText(
  model: JornadaTicketModel,
  w: number,
): string[] {
  const caja = model.cajas[0];
  const lines: string[] = [];

  appendHeader(lines, w, "RESUMEN GLOBAL DE JORNADA");
  lines.push(centered(model.dayLabel, w));
  lines.push(blank(w));

  lines.push(metaRow("Caja:", fitCell(caja.puntoCajaNombre, w - 6), w));
  lines.push(
    metaRow("Responsables:", fitCell(model.responsablesLabel, w - 14), w),
  );
  lines.push(
    metaRow(
      "Horario:",
      caja.operando
        ? `${caja.horarioInicio} - Operando`
        : `${caja.horarioInicio} - ${caja.horarioFin}`,
      w,
    ),
  );
  lines.push(
    metaRow(
      "Turnos:",
      `${caja.turnos.length} ${caja.turnos.length === 1 ? "turno" : "turnos"}`,
      w,
    ),
  );
  lines.push(blank(w));

  for (const turno of caja.turnos) {
    appendTurnoResumen(lines, turno, w);
  }

  sectionSub(lines, "INGRESOS POR MÉTODO (TOTAL DE LA CAJA)", w);
  appendMetodosTable(lines, caja.montosPorMetodo, w, {
    incluirSubtotalesCanal: true,
    subtotalLabel: "TOTAL COBRADO EN VENTAS",
  });
  if (caja.recaudoTotal > caja.subtotalCobrado) {
    lines.push(
      metaRow(
        labelForWidth(
          "Recaudo total (incl. ingresos/abonos)",
          "Recaudo total (incl.)",
          w,
        ),
        moneyTicket(caja.recaudoTotal),
        w,
      ),
    );
  }
  lines.push(blank(w));

  return lines;
}

function appendMetodosTable(
  lines: string[],
  montos: MontosPorMetodoPago,
  w: number,
  opts?: {
    incluirSubtotalesCanal?: boolean;
    subtotalLabel?: string;
  },
): void {
  const rows = METODOS_PROPINA.filter((k) => montos[k] > 0).map((k) => {
    const label = getDefaultPaymentMethodLabel(k);
    return {
      left: labelForWidth(label, label.slice(0, 4), w),
      right: moneyTicket(montos[k]),
    };
  });

  const footerGroups: { left: string; right: string }[][] = [];

  if (opts?.incluirSubtotalesCanal) {
    const canal: { left: string; right: string }[] = [];
    if (montos.efectivo > 0) {
      canal.push({
        left: labelForWidth("Total Efectivo (Ventas)", "Tot.Efec.(Ventas)", w),
        right: moneyTicket(montos.efectivo),
      });
    }
    const dig = digitalTotal(montos);
    if (dig > 0) {
      canal.push({
        left: labelForWidth("Total Digital (Ventas)", "Tot.Digital(Vent.", w),
        right: moneyTicket(dig),
      });
    }
    if (canal.length > 0) footerGroups.push(canal);
  }

  footerGroups.push([
    {
      left: opts?.subtotalLabel ?? "SUBTOTAL",
      right: moneyTicket(subtotalMontos(montos)),
    },
  ]);

  lines.push(
    ...table2Col(
      w,
      labelForWidth("Método de Pago", "Método", w),
      "Total",
      rows,
      { footerGroups, closeBottom: false },
    ),
  );
}

function appendCajaBlock(
  lines: string[],
  caja: JornadaCajaTicketBlock,
  w: number,
  narrow: boolean,
  includeTurnos: boolean,
): void {
  sectionSub(lines, caja.ticketLabel, w);

  lines.push(
    metaRow(
      "Horario:",
      caja.operando
        ? `${caja.horarioInicio} - Operando`
        : `${caja.horarioInicio} - ${caja.horarioFin}`,
      w,
    ),
  );
  lines.push(metaRow("Ventas:", `${caja.ventasTotal} v`, w));
  if (caja.diferencia !== 0) {
    const sign = caja.diferencia > 0 ? "+" : "";
    lines.push(
      metaRow(
        "Descuadre:",
        `${sign}${Math.abs(caja.diferencia).toLocaleString("es-CO")}`,
        w,
      ),
    );
  }
  lines.push(blank(w));

  appendMetodosTable(lines, caja.montosPorMetodo, w);
  lines.push(blank(w));

  if (!includeTurnos || caja.turnos.length <= 1) return;

  sectionSub(
    lines,
    labelForWidth(`TURNOS (${caja.turnos.length})`, "TURNOS", w),
    w,
  );

  const turnoRows = caja.turnos.map((turno) => {
    const leftFull = `${turno.turnoLabel} - ${turno.responsable}`;
    const leftNarrow = fitCell(turno.turnoLabel, 14);
    return {
      left: narrow ? leftNarrow : leftFull,
      right: moneyTicket(turno.subtotalCobrado),
    };
  });

  const difRows = caja.turnos
    .filter((t) => t.diferencia !== 0)
    .map((t) => {
      const sign = t.diferencia > 0 ? "+" : "-";
      return {
        left: labelForWidth(
          `Dif. ${t.turnoLabel}`,
          `Dif.${fitCell(t.turnoLabel, 6)}`,
          w,
        ),
        right: `${sign}${Math.abs(t.diferencia).toLocaleString("es-CO")}`,
      };
    });

  lines.push(
    ...table2Col(
      w,
      labelForWidth("Turno", "Turno", w),
      labelForWidth("Cobrado", "Total", w),
      turnoRows,
      {
        footerRows: difRows.length > 0 ? difRows : undefined,
        closeBottom: false,
      },
    ),
  );
  lines.push(blank(w));
}

export function buildJornadaTicketPlainText(
  input: BuildJornadaTicketPlainTextInput,
): string[] {
  const { model } = input;
  const w = ticketCols(input.paperWidthMm ?? 80);
  const narrow = isNarrowTicket(w);
  const includeTurnos = input.includeTurnos ?? true;
  const biz = loadBusinessForReceipt();

  const lines: string[] = [];

  // Variante de caja única (usada desde «Ver resumen global»): muestra el
  // resumen del turno consolidado de esa caja, no el agregado multicaja.
  if (model.cajasCount === 1) {
    lines.push(...buildSingleCajaPlainText(model, w));
    lines.push(minorSep(w));
    lines.push(centered("Gestionado con", w));
    lines.push(centered("ALTENOT", w));
    lines.push(
      centered(
        labelForWidth(
          "No editable - comprobante oficial",
          "No editable - comprobante",
          w,
        ),
        w,
      ),
    );
    return lines;
  }

  lines.push(majorSep(w));
  lines.push(centered(biz.nombre.toUpperCase(), w));
  if (biz.slogan) lines.push(centered(biz.slogan.toUpperCase(), w));
  lines.push(majorSep(w));
  lines.push(centered("RESUMEN GLOBAL DE JORNADA", w));
  lines.push(centered(model.dayLabel, w));
  lines.push(centered(model.generatedAtLabel, w));
  lines.push(blank(w));

  lines.push(metaRow("Jornada:", model.dayLabel, w));
  lines.push(
    metaRow(
      "Horario:",
      model.operando
        ? `${model.horarioInicio} - Operando`
        : `${model.horarioInicio} - ${model.horarioFin}`,
      w,
    ),
  );
  lines.push(
    metaRow(
      "Cajas/Turnos:",
      `${model.cajasCount} cajas - ${model.turnosCount} turnos`,
      w,
    ),
  );
  lines.push(
    metaRow("Responsables:", fitCell(model.responsablesLabel, w - 14), w),
  );
  lines.push(blank(w));

  sectionMajor(lines, "TOTAL DEL DÍA", w);
  const consolidadoRows = [
    {
      left: "Base Inicial",
      right: moneyTicket(model.consolidado.baseInicial),
    },
    {
      left: labelForWidth("Ventas cobradas", "Vtas.cobradas", w),
      right: moneyTicket(model.consolidado.totalVentas),
    },
    {
      left: labelForWidth("Esperado gaveta", "Esperado", w),
      right: moneyTicket(model.consolidado.totalEsperado),
    },
    {
      left: "Contado Real",
      right: moneyTicket(model.consolidado.totalContado),
    },
  ];
  if (model.consolidado.descuadreNeto !== 0) {
    const sign = model.consolidado.descuadreNeto > 0 ? "+" : "";
    consolidadoRows.push({
      left: labelForWidth("Descuadre neto", "Descuadre", w),
      right: `${sign}${Math.abs(model.consolidado.descuadreNeto).toLocaleString("es-CO")}`,
    });
  }
  consolidadoRows.push({
    left: `Ventas (${model.consolidado.ventasTotal})`,
    right: `${model.consolidado.cerradas} cierres`,
  });
  if (model.totalGastosExternosDia > 0) {
    consolidadoRows.push({
      left: "(−) GASTOS DEL DUENO (DIA)",
      right: `-${moneyTicket(model.totalGastosExternosDia)}`,
    });
    consolidadoRows.push({
      left: "UTILIDAD NETA ESTIMADA",
      right: moneyTicket(model.utilidadNetaDia),
    });
  }
  lines.push(
    ...table2Col(w, "Concepto", "Valor", consolidadoRows, {
      closeBottom: false,
    }),
  );
  lines.push(blank(w));

  if (model.totalGastosExternosDia > 0) {
    sectionSub(lines, "GASTOS DEL DUENO (FUERA DE CAJA)", w);
    for (const g of model.gastosExternosDia) {
      const concepto = `${g.categoria}${g.descripcion ? ` · ${g.descripcion}` : ""}`;
      const metodo = gastoExternoMetodoLabel(g.metodoPago, g.metodoPagoLabel);
      lines.push(
        ...table2Col(w, "Concepto", "Monto", [
          {
            left: labelForWidth(`${concepto} (${metodo})`, concepto, w),
            right: `-${moneyTicket(g.monto)}`,
          },
        ]),
      );
    }
    lines.push(blank(w));
  }

  sectionMajor(lines, "COBRADO DEL DÍA POR MÉTODO", w);
  appendMetodosTable(lines, model.ventasDiaPorMetodo, w, {
    incluirSubtotalesCanal: true,
  });
  lines.push(blank(w));

  sectionMajor(lines, "RESUMEN DE CAJAS", w);
  for (const caja of model.cajas) {
    appendCajaBlock(lines, caja, w, narrow, includeTurnos);
  }

  sectionSub(lines, "TOTAL RECAUDADO", w);
  lines.push(
    ...table2Col(
      w,
      labelForWidth("Descripción", "Desc.", w),
      "Valor",
      [
        {
          left: labelForWidth(
            "Total recaudado (Efec + Dig)",
            "TOTAL RECAUDADO",
            w,
          ),
          right: moneyTicket(model.totalRecaudado),
        },
      ],
      { closeBottom: false },
    ),
  );
  lines.push(blank(w));

  lines.push(minorSep(w));
  lines.push(centered("Gestionado con", w));
  lines.push(centered("ALTENOT", w));
  lines.push(
    centered(
      labelForWidth(
        "No editable - comprobante oficial",
        "No editable - comprobante",
        w,
      ),
      w,
    ),
  );

  return lines;
}
